import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Bell, LogOut, Menu, X, User, ChevronDown, Sun, Moon, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useCluster } from '../context/ClusterContext';
import { useTheme } from '../context/ThemeContext';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { clusters, selectedCluster, setSelectedCluster } = useCluster();
  const { theme, toggleTheme } = useTheme();
  const [timeRangeLabel, setTimeRangeLabel] = useState('Last 24 Hours');

  const TIME_RANGE_OPTIONS = [
    { label: 'Last 1 Hour',   hours: 1 },
    { label: 'Last 3 Hours',  hours: 3 },
    { label: 'Last 6 Hours',  hours: 6 },
    { label: 'Last 12 Hours', hours: 12 },
    { label: 'Last 24 Hours', hours: 24 },
    { label: 'Last 2 Days',   hours: 48 },
    { label: 'Last 7 Days',   hours: 168 },
    { label: 'Last 30 Days',  hours: 720 },
    { label: 'Last 45 Days',  hours: 1080 },
  ];

  const handleTimeRangeSelect = (option) => {
    setTimeRangeLabel(option.label);
    window.dispatchEvent(new CustomEvent('monitoring-time-range-change', { detail: { hours: option.hours } }));
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data.filter(a => !a.read));
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    window.location.href = '/login';
  };

  const navSections = [
    {
      label: 'Overview',
      items: [
        { path: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
        { path: '/clusters', icon: 'dns', label: 'Clusters' },
      ],
    },
    {
      label: 'Optimize',
      items: [
        { path: '/rightsizing', icon: 'balance', label: 'Rightsizing' },
        { path: '/node-optimization', icon: 'memory', label: 'Node Optimization' },
        { path: '/commitments', icon: 'savings', label: 'Commitments' },
        { path: '/serverless', icon: 'bedtime', label: 'Serverless' },
        { path: '/scheduler-policies', icon: 'tune', label: 'Scheduler Policies' },
      ],
    },
    {
      label: 'Insights',
      items: [
        { path: '/monitoring', icon: 'monitoring', label: 'Monitoring' },
        // Cost Report + Realizable Savings hidden from the nav: Monitoring now
        // covers cost reporting, and savings live on Node Optimization — three
        // entry points to overlapping numbers is how a dashboard starts
        // contradicting itself. `hidden` (rather than deleting the entries)
        // keeps the routes reachable by direct link AND keeps the page-title
        // resolver below working — it indexes this same list by path.
        { path: '/cost-report', icon: 'receipt_long', label: 'Cost Report', hidden: true },
        { path: '/realizable-savings', icon: 'savings', label: 'Realizable Savings', hidden: true },
        // Savings Report hidden — numbers need more validation before
        // we show them to customers. Re-enable when confident.
        // { path: '/savings-report', icon: 'savings', label: 'Savings Report' },
        { path: '/forecasting', icon: 'trending_up', label: 'Forecasting' },
      ],
    },
  ];

  // Flat list kept for the page-title resolver and any callers that
  // still index by path.
  const navItems = navSections.flatMap((s) => s.items);
  const settingsItems = [];

  return (
    <TooltipProvider>
    <div className="flex h-screen w-full overflow-hidden bg-background-light dark:bg-background-dark text-slate-900 dark:text-white font-display">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:relative top-0 left-0 h-full w-64
        bg-background-light dark:bg-background-dark border-r border-gray-200 dark:border-surface-border
        flex flex-col z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        shrink-0
      `}>
        {/* Brand — compact, left-aligned. No centered hero block, no tagline. */}
        <div className="px-5 pt-6 pb-5 flex items-center gap-3" id="tour-logo">
          <img
            src="/optops-logo.png"
            alt="OptOps"
            className="h-12 w-auto dark:invert dark:hue-rotate-180"
          />
          <span className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            OptOps<span className="text-zinc-400 dark:text-zinc-500 font-medium">.Ai</span>
          </span>
        </div>

        {/* Navigation — grouped sections + thin left accent bar on active. */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {navSections.map((section, sIdx) => (
            <div key={section.label} className={sIdx === 0 ? 'mt-2' : 'mt-5'}>
              <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.08em] text-zinc-400 dark:text-zinc-500 uppercase">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.filter((item) => !item.hidden).map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }}
                      className={`
                        group relative w-full flex items-center gap-3 pl-3 pr-3 py-2 rounded-md
                        transition-all duration-150
                        ${isActive
                          ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-800/60'}
                      `}
                      id={`tour-sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {/* Left accent bar — appears only on active. */}
                      <span
                        className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full transition-opacity ${
                          isActive
                            ? 'bg-zinc-900 dark:bg-white opacity-100'
                            : 'bg-zinc-900 dark:bg-white opacity-0'
                        }`}
                      />
                      <span
                        className={`material-symbols-outlined text-[20px] transition-transform duration-150 group-hover:scale-105 ${
                          isActive ? 'text-zinc-900 dark:text-white' : ''
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span className={`text-sm ${isActive ? 'font-medium' : 'font-normal'}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: Docs + legal links */}
        <div className="px-3 py-3 border-t border-zinc-200 dark:border-zinc-800">
          <button
            onClick={() => { navigate('/docs'); setSidebarOpen(false); }}
            className={`
              group relative w-full flex items-center gap-3 pl-3 pr-3 py-2 rounded-md
              transition-all duration-150
              ${location.pathname === '/docs'
                ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-800/60'}
            `}
          >
            <span
              className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full transition-opacity ${
                location.pathname === '/docs'
                  ? 'bg-zinc-900 dark:bg-white opacity-100'
                  : 'bg-zinc-900 dark:bg-white opacity-0'
              }`}
            />
            <span className="material-symbols-outlined text-[20px] transition-transform duration-150 group-hover:scale-105">menu_book</span>
            <span className="text-sm">Documentation</span>
          </button>
          <div className="flex items-center gap-2.5 px-3 pt-3 text-[11px] text-zinc-400 dark:text-zinc-600">
            <a
              href="https://optops.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
            >
              Privacy
            </a>
            <span className="text-zinc-300 dark:text-zinc-700">·</span>
            <a
              href="https://optops.ai/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
            >
              Terms
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-surface-border bg-background-light dark:bg-background-dark z-10 shrink-0">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="md:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>

            <h2 className="text-lg font-bold dark:text-white text-slate-900">
              {navItems.find(i => i.path === location.pathname)?.label ||
                settingsItems.find(i => i.path === location.pathname)?.label ||
                'Dashboard Overview'}
            </h2>

            <div className="h-6 w-px bg-gray-300 dark:bg-surface-border mx-2 hidden sm:block" />

            {/* Cluster Selector */}
            <div className="relative hidden sm:flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-text-secondary hover:text-slate-900 dark:hover:text-white transition-colors"
                    id="tour-header-cluster-select"
                  >
                    <span className="material-symbols-outlined text-[20px]">cloud</span>
                    {selectedCluster ? selectedCluster.name : 'All Clusters'}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onSelect={() => setSelectedCluster(null)}>
                    All Clusters
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {clusters.map((cluster) => (
                    <DropdownMenuItem key={cluster.id} onSelect={() => setSelectedCluster(cluster)}>
                      <span className={`mr-2 w-2 h-2 rounded-full ${cluster.status === 'healthy' ? 'bg-green-500' : 'bg-yellow-400'}`} />
                      {cluster.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Date Range */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border text-sm font-medium text-slate-700 dark:text-text-secondary hover:border-primary transition-colors">
                  <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                  {timeRangeLabel}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {TIME_RANGE_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.hours}
                    onClick={() => handleTimeRangeSelect(opt)}
                    className={timeRangeLabel === opt.label ? 'font-semibold text-primary' : ''}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Guided Tour Trigger */}
            <UITooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => window.dispatchEvent(new CustomEvent('restart-optops-tour'))}
                  className="text-slate-500 dark:text-text-secondary hover:bg-slate-200 dark:hover:bg-surface-border transition-colors group relative"
                >
                  <HelpCircle className="w-5 h-5 group-hover:text-primary transition-colors" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-slate-800 text-white border-slate-700">
                <p className="text-[11px] font-medium">Restart Guided Tour</p>
              </TooltipContent>
            </UITooltip>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-slate-500 dark:text-text-secondary hover:bg-slate-200 dark:hover:bg-surface-border transition-colors"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative text-slate-500 dark:text-text-secondary hover:bg-slate-200 dark:hover:bg-surface-border">
                  <Bell className="w-5 h-5" />
                  {alerts.length > 0 && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-background-light dark:border-background-dark" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                {alerts.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">
                    No new alerts
                  </div>
                ) : (
                  alerts.slice(0, 5).map((alert) => (
                    <DropdownMenuItem key={alert.id} className="flex-col items-start p-3">
                      <div className="font-medium text-slate-900">{alert.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{alert.message}</div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2">
                  <div
                    className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm ring-2 ring-gray-100 cursor-pointer"
                  >
                    {user?.full_name?.[0] || 'U'}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-background-light dark:bg-background-dark">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </div>
      </main>
    </div>
    </TooltipProvider>
  );
}

export default Layout;
